import { Info as InfoIcon } from "@mui/icons-material";
import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import WordGrid from "./WordGrid";
import { helloWordDirections, bonjourWordDirections } from "./sample";
import { getContentLanguage } from "../../../services/LangPrefs";
import { usePagesTranslation } from "../../locales";

/** @typedef {"both" | "crossword" | "wordSearch"} WordDirectionsMode */

/**
 * @param {string} [language] — "english" | "french"; defaults to account content language
 * @param {boolean} [embedded] — tighter margins; subsection title on how-to pages
 * @param {WordDirectionsMode} [mode] — "both" (default): two columns; "crossword" / "wordSearch": single column for how-to
 */
const WordDirectionsSection = ({ language, embedded = false, mode = "both" }) => {
    const { t } = usePagesTranslation();
    const contentLanguage = language ?? getContentLanguage();
    const wordDirections = contentLanguage === "french" ? bonjourWordDirections : helloWordDirections;
    const exampleWord = contentLanguage === "french" ? "BONJOUR" : "HELLO";

    const showCrossword = mode === "both" || mode === "crossword";
    const showWordSearch = mode === "both" || mode === "wordSearch";

    const [currentIndex, setCurrentIndex] = useState(0);
    const [characterIndex, setCharacterIndex] = useState(-1);
    const [crosswordIndex, setCrosswordIndex] = useState(0);
    const [crosswordCharacterIndex, setCrosswordCharacterIndex] = useState(-1);

    // Reset animation indices when language or mode changes
    useEffect(() => {
        setCurrentIndex(0);
        setCrosswordIndex(0);
        setCharacterIndex(-1);
        setCrosswordCharacterIndex(-1);
    }, [contentLanguage, mode]);

    const crosswordDirections = useMemo(() => {
        return wordDirections.filter(word =>
            word.direction === "horizontal-right" ||
            word.direction === "vertical-down"
        );
    }, [wordDirections]);

    useEffect(() => {
        if (!showWordSearch) return;
        const currentWord = wordDirections[currentIndex];
        const wordLength = currentWord?.positions?.length || 0;
        setCharacterIndex(-1);
        let charTimeout;
        const showNextCharacter = (index) => {
            if (index < wordLength) {
                setCharacterIndex(index);
                charTimeout = setTimeout(() => {
                    showNextCharacter(index + 1);
                }, 500);
            } else {
                setTimeout(() => {
                    setCurrentIndex((prevIndex) => (prevIndex + 1) % wordDirections.length);
                }, 3000);
            }
        };
        showNextCharacter(0);
        return () => {
            if (charTimeout) clearTimeout(charTimeout);
        };
    }, [currentIndex, wordDirections, showWordSearch]);

    useEffect(() => {
        if (!showCrossword) return;
        const currentWord = crosswordDirections[crosswordIndex];
        const wordLength = currentWord?.positions?.length || 0;
        setCrosswordCharacterIndex(-1);
        let charTimeout;
        const showNextCharacter = (index) => {
            if (index < wordLength) {
                setCrosswordCharacterIndex(index);
                charTimeout = setTimeout(() => {
                    showNextCharacter(index + 1);
                }, 500);
            } else {
                setTimeout(() => {
                    setCrosswordIndex((prevIndex) => (prevIndex + 1) % crosswordDirections.length);
                }, 3000);
            }
        };
        showNextCharacter(0);
        return () => {
            if (charTimeout) clearTimeout(charTimeout);
        };
    }, [crosswordIndex, crosswordDirections, showCrossword]);

    const currentWord = wordDirections[currentIndex];
    const currentCrosswordWord = crosswordDirections[crosswordIndex];

    const mdCol = mode === "both" ? 6 : 12;

    const titleKey =
        embedded && (mode === "wordSearch" || mode === "crossword")
            ? "landing.wordDirections.title-short"
            : "landing.wordDirections.title";

    const titleTypography = (
        <Typography
            variant={embedded ? "h5" : "h4"}
            component={embedded ? "h3" : "h2"}
            gutterBottom
            sx={{
                textAlign: embedded ? "left" : "center",
                mb: embedded ? 2 : 4,
                fontWeight: 600,
                ...(!embedded ? {} : { color: "text.primary" }),
            }}
        >
            {t(titleKey)}
        </Typography>
    );

    const crosswordInfoPanel = (
        <Box
            sx={{
                flex: 1,
                width: "100%",
                boxSizing: "border-box",
                p: 2,
                borderRadius: 1,
                backgroundColor: "#e3f2fd",
                border: "1px solid",
                borderColor: "#2196f3",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
            }}
        >
            <InfoIcon sx={{ fontSize: 22, color: "#2196f3", flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {t("landing.wordDirections.directions-2-intro")}
                </Typography>
                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>{t("landing.wordDirections.horizontal")}</strong> {t("landing.wordDirections.horizontal-lr")}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>{t("landing.wordDirections.vertical")}</strong> {t("landing.wordDirections.vertical-tb")}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    const wordSearchInfoPanel = (
        <Box
            sx={{
                flex: 1,
                width: "100%",
                boxSizing: "border-box",
                p: 2,
                borderRadius: 1,
                backgroundColor: "#e3f2fd",
                border: "1px solid",
                borderColor: "#2196f3",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
            }}
        >
            <InfoIcon sx={{ fontSize: 22, color: "#2196f3", flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    {t("landing.wordDirections.directions-8-intro")}
                </Typography>
                <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>{t("landing.wordDirections.horizontal")}</strong> {t("landing.wordDirections.horizontal-full")}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>{t("landing.wordDirections.vertical")}</strong> {t("landing.wordDirections.vertical-full")}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>{t("landing.wordDirections.diagonal")}</strong> {t("landing.wordDirections.diagonal-full")}
                    </Typography>
                    <Typography variant="body2">
                        • {t("landing.wordDirections.reversed")}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ mt: embedded ? 0 : 6, mb: embedded ? 0 : 6 }}>
            {titleTypography}

            <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
                {showCrossword && (
                    <Grid
                        size={{ xs: 12, md: mdCol }}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                            gap: mode === "crossword" && embedded ? 0 : 1.5,
                        }}
                    >
                        {mode === "crossword" && embedded ? (
                            <>
                                <Typography variant="body1" color="text.secondary" sx={{ textAlign: "left", mb: 2 }}>
                                    {t("landing.wordDirections.crossword-intro", { word: exampleWord })}
                                </Typography>
                                <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
                                    <Grid
                                        size={{ xs: 12, md: 6 }}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <WordGrid gridSize={12} cellSize={24} currentWord={currentCrosswordWord} characterIndex={crosswordCharacterIndex} />
                                    </Grid>
                                    <Grid
                                        size={{ xs: 12, md: 6 }}
                                        sx={{
                                            display: "flex",
                                            alignItems: "stretch",
                                            minWidth: 0,
                                        }}
                                    >
                                        {crosswordInfoPanel}
                                    </Grid>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Typography variant="h5" sx={{ textAlign: embedded ? "left" : "center", fontWeight: 600 }}>
                                    {t("landing.wordDirections.crossword-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ textAlign: embedded ? "left" : "center" }}>
                                    {t("landing.wordDirections.crossword-intro", { word: exampleWord })}
                                </Typography>
                                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                    <WordGrid gridSize={12} cellSize={24} currentWord={currentCrosswordWord} characterIndex={crosswordCharacterIndex} />
                                </Box>
                                {crosswordInfoPanel}
                            </>
                        )}
                    </Grid>
                )}

                {showWordSearch && (
                    <Grid
                        size={{ xs: 12, md: mdCol }}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                            gap: mode === "wordSearch" && embedded ? 0 : 1.5,
                        }}
                    >
                        {mode === "wordSearch" && embedded ? (
                            <>
                                <Typography variant="body1" color="text.secondary" sx={{ textAlign: "left", mb: 2 }}>
                                    {t("landing.wordDirections.word-search-intro", { word: exampleWord })}
                                </Typography>
                                <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
                                    <Grid
                                        size={{ xs: 12, md: 6 }}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <WordGrid gridSize={12} cellSize={24} currentWord={currentWord} characterIndex={characterIndex} />
                                    </Grid>
                                    <Grid
                                        size={{ xs: 12, md: 6 }}
                                        sx={{
                                            display: "flex",
                                            alignItems: "stretch",
                                            minWidth: 0,
                                        }}
                                    >
                                        {wordSearchInfoPanel}
                                    </Grid>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Typography variant="h5" sx={{ textAlign: embedded ? "left" : "center", fontWeight: 600 }}>
                                    {t("landing.wordDirections.word-search-title")}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ textAlign: embedded ? "left" : "center" }}>
                                    {t("landing.wordDirections.word-search-intro", { word: exampleWord })}
                                </Typography>
                                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                    <WordGrid gridSize={12} cellSize={24} currentWord={currentWord} characterIndex={characterIndex} />
                                </Box>
                                {wordSearchInfoPanel}
                            </>
                        )}
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default WordDirectionsSection;
